/** Query page-local rows without changing the underlying fixture array. */
export function queryAdminRows(
  rows,
  { query = '', status = '', sortKey = '', direction = 'asc', page = 1, pageSize = 10 } = {}
) {
  const search = query.trim().toLocaleLowerCase();
  const filtered = rows.filter(
    (row) =>
      (!status || row.status.toLocaleLowerCase() === status.toLocaleLowerCase()) &&
      (!search || Object.values(row).some((value) => String(value).toLocaleLowerCase().includes(search)))
  );
  if (sortKey)
    filtered.sort(
      (a, b) =>
        String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? ''), undefined, {
          numeric: true,
          sensitivity: 'base'
        }) * (direction === 'desc' ? -1 : 1)
    );
  const size = Math.max(1, Number(pageSize) || 10);
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const current = Math.max(1, Math.min(Number(page) || 1, pages));
  return { rows: filtered.slice((current - 1) * size, current * size), total: filtered.length, page: current, pages };
}

export function validateAdminUser(values, users, excludeId) {
  const value = {
    name: String(values.name || '').trim(),
    email: String(values.email || '')
      .trim()
      .toLocaleLowerCase(),
    role: String(values.role || 'User'),
    status: String(values.status || 'Active')
  };
  const errors = {};
  if (!value.name) errors.name = 'Enter a name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) errors.email = 'Enter a valid email address.';
  else if (users.some((user) => user.id !== excludeId && user.email.toLocaleLowerCase() === value.email))
    errors.email = 'This email already exists in this demo.';
  return { value, errors };
}

// Rollup captures this module URL for both classic scripts and ES modules.
const adminModuleURL = import.meta.url;
export function resolveAdminAsset(value, moduleURL = adminModuleURL) {
  if (!value || /^(?:[a-z][a-z\d+.-]*:|\/\/|#)/i.test(value)) return value;
  const local = value.replace(/^\/?assets\//, '');
  return moduleURL ? new URL(`../assets/${local}`, moduleURL).href : value;
}

const initialized = new WeakSet();
const fixtureCreators = new WeakMap();
let fixtureIdentifier = 0;
const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const button = (text, className = 'btn-outline theme-secondary btn-sm') => {
  const node = el('button', className, text);
  node.type = 'button';
  return node;
};

export function initializeAdmin(root = document, api = window.AsteroUI) {
  if (!document.documentElement.classList.contains('astero-admin') || initialized.has(root)) return;
  initialized.add(root);
  const announce = (message) => {
    const status = document.getElementById('admin-status');
    if (status) status.textContent = message;
  };
  initializeShell(root, api, announce);
  initializeUsers(root, api, announce);
  initializeFixtureTables(root, api, announce);
  initializeForms(root, api, announce);
  initializeMedia(root, api, announce);
  initializeFixtureActions(root, api, announce);
}

function initializeShell(root, api, announce) {
  const sidebar = root.querySelector('#admin-sidebar');
  const toggle = root.querySelector('#sidebar-toggle');
  const desktop = window.matchMedia('(min-width: 992px)');
  if (sidebar && toggle) {
    const sync = () => {
      const expanded = desktop.matches ? document.documentElement.dataset.adminSidebar !== 'mini' : sidebar.open;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute(
        'aria-label',
        desktop.matches
          ? expanded
            ? 'Collapse navigation'
            : 'Expand navigation'
          : expanded
            ? 'Close navigation'
            : 'Open navigation'
      );
    };
    toggle.addEventListener('click', () => {
      if (desktop.matches) {
        document.documentElement.dataset.adminSidebar =
          document.documentElement.dataset.adminSidebar === 'mini' ? 'expanded' : 'mini';
        try {
          localStorage.setItem('astero-admin-sidebar', document.documentElement.dataset.adminSidebar);
        } catch {
          /* Storage is optional. */
        }
      } else api.Drawer.getOrCreateInstance(sidebar).toggle(toggle);
      sync();
    });
    ['shown.bs.drawer', 'hidden.bs.drawer', 'close'].forEach((event) => sidebar.addEventListener(event, sync));
    desktop.addEventListener('change', () => {
      if (sidebar.open) api.Drawer.getOrCreateInstance(sidebar).hide();
      sync();
    });
    sync();
  }
  const command = root.querySelector('#admin-command');
  const search = root.querySelector('#admin-command-input');
  if (command && search) {
    const links = [...command.querySelectorAll('[data-command-item]')];
    search.addEventListener('input', () => {
      let matches = 0;
      for (const link of links) {
        link.hidden = !link.textContent.toLocaleLowerCase().includes(search.value.trim().toLocaleLowerCase());
        if (!link.hidden) matches++;
      }
      command.querySelector('[data-command-empty]').hidden = matches > 0;
    });
    search.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        const first = links.find((link) => !link.hidden);
        if (first) {
          event.preventDefault();
          if (event.key === 'Enter') first.click();
          else first.focus();
        }
      }
    });
    document.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        api.Dialog.getOrCreateInstance(command).toggle(document.activeElement);
      }
    });
    command.addEventListener('shown.bs.dialog', () => search.focus());
  }
  root.querySelectorAll('[data-notification-dismiss]').forEach((control) =>
    control.addEventListener('click', (event) => {
      event.stopPropagation();
      control.closest('[data-notification]').remove();
      if (!root.querySelector('[data-notification]')) {
        root.querySelector('[data-notifications-empty]').hidden = false;
        root.querySelector('[data-notification-indicator]').hidden = true;
      }
      announce('Notification dismissed in this page’s demo.');
    })
  );
  root.querySelector('[data-notifications-read]')?.addEventListener('click', (event) => {
    event.stopPropagation();
    root.querySelector('[data-notification-indicator]').hidden = true;
    announce('All demo notifications marked as read on this page.');
    event.currentTarget.textContent = 'All read';
  });
}

// Native Menu can reparent its contents while hiding. Finish that lifecycle
// before native Dialog chooses focus, and return to a visible menu trigger.
async function prepareAdminDialog(api, trigger, previousDialog) {
  const toggles = [...document.querySelectorAll('[data-bs-toggle="menu"][aria-expanded="true"]')];
  const focusTarget = trigger?.closest('.menu') ? toggles.at(-1) || trigger : trigger;
  await Promise.all(toggles.map((toggle) => api.Menu.getInstance(toggle)?.hide()));
  if (previousDialog?.open) await api.Dialog.getOrCreateInstance(previousDialog).hide();
  return focusTarget;
}

async function showAdminDialog(api, dialog, trigger) {
  await api.Dialog.getOrCreateInstance(dialog).show(trigger);
  // The alpha's animated visibility can defer the browser's initial focus.
  // Repair only initial focus after native shown; trapping remains native.
  if (!dialog.contains(document.activeElement)) {
    const control =
      dialog.querySelector('[autofocus]') ||
      dialog.querySelector(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]'
      );
    control?.focus({ preventScroll: true });
  }
}

function initializeUsers(root, api, announce) {
  const container = root.querySelector('[data-admin-users]');
  if (!container) return;
  let users = JSON.parse(container.querySelector('[data-users-fixture]').textContent);
  const selected = new Set();
  const state = { query: '', status: '', sortKey: '', direction: 'asc', page: 1, pageSize: 10 };
  const body = container.querySelector('[data-users-body]');
  const selectAll = container.querySelector('[data-users-select-all]');
  const feedback = (message) => {
    container.querySelector('[data-users-feedback]').textContent = message;
    announce(message);
  };
  const view = root.querySelector('#admin-user-view');
  const editor = root.querySelector('#admin-user-editor');
  const deletion = root.querySelector('#admin-user-delete');
  const form = editor.querySelector('form');
  let editingId = null;
  let currentId = null;
  let deleteIds = [];
  let returnControl = null;
  for (const dialog of [view, editor, deletion])
    dialog.addEventListener('hidden.bs.dialog', () => {
      if (!document.querySelector('dialog[open]'))
        (returnControl?.isConnected ? returnControl : root.querySelector('[data-user-add]'))?.focus();
    });
  const showEditor = async (id, trigger) => {
    editingId = id;
    returnControl = trigger || returnControl;
    form.reset();
    for (const node of form.querySelectorAll('[data-user-error]')) node.textContent = '';
    for (const node of form.querySelectorAll('[aria-invalid]')) node.removeAttribute('aria-invalid');
    editor.querySelector('h2').textContent = id === null ? 'Add user' : 'Edit user';
    const user = users.find((row) => row.id === id) || { name: '', email: '', role: 'User', status: 'Active' };
    for (const key of ['name', 'email', 'role', 'status']) form.elements.namedItem(key).value = user[key];
    returnControl = await prepareAdminDialog(api, trigger || returnControl, view);
    await showAdminDialog(api, editor, returnControl);
  };
  const showView = async (id, trigger) => {
    currentId = id;
    returnControl = trigger;
    const user = users.find((row) => row.id === id);
    const details = view.querySelector('[data-user-details]');
    details.replaceChildren();
    for (const [key, label] of [
      ['name', 'Name'],
      ['email', 'Email'],
      ['role', 'Role'],
      ['status', 'Status'],
      ['lastSeen', 'Last active']
    ])
      details.append(el('dt', '', label), el('dd', '', user[key]));
    returnControl = await prepareAdminDialog(api, trigger);
    await showAdminDialog(api, view, returnControl);
  };
  const confirmDelete = async (ids, trigger) => {
    deleteIds = [...ids];
    returnControl = trigger;
    deletion.querySelector('[data-users-delete-description]').textContent =
      ids.length === 1
        ? `Delete ${users.find((user) => user.id === ids[0]).name} from this demo directory?`
        : `Delete ${ids.length} selected users from this demo directory?`;
    returnControl = await prepareAdminDialog(api, trigger);
    await showAdminDialog(api, deletion, returnControl);
  };
  const render = () => {
    const result = queryAdminRows(users, state);
    state.page = result.page;
    body.querySelectorAll('[data-bs-toggle="menu"]').forEach((toggle) => api.Menu.getInstance(toggle)?.dispose());
    body.replaceChildren();
    for (const user of result.rows) {
      const row = el('tr');
      row.dataset.userId = user.id;
      const checkCell = el('td');
      const check = el('input', 'check');
      check.type = 'checkbox';
      check.checked = selected.has(user.id);
      check.dataset.userSelect = user.id;
      check.setAttribute('aria-label', `Select ${user.name}`);
      checkCell.append(check);
      row.append(checkCell);
      const nameCell = el('td');
      const identity = el('div', 'admin-user-cell');
      const avatar = el('img');
      avatar.src = resolveAdminAsset(`/assets/images/avatars/avatar${user.avatar || 1}.jpg`);
      avatar.alt = '';
      avatar.width = 30;
      avatar.height = 30;
      identity.append(avatar, el('strong', '', user.name));
      nameCell.append(identity);
      row.append(nameCell, el('td', '', user.email), el('td', '', user.role));
      const statusCell = el('td');
      const badge = el('span', 'admin-status-badge', user.status);
      badge.dataset.status = user.status;
      statusCell.append(badge);
      row.append(statusCell, el('td', 'fg-2', user.lastSeen));
      const actions = el('td');
      const menuWrapper = el('div');
      const toggle = button('•••', 'btn-text admin-icon');
      toggle.dataset.bsToggle = 'menu';
      toggle.dataset.bsPlacement = 'bottom-end';
      toggle.dataset.bsContainer = 'body';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', `Actions for ${user.name}`);
      const menu = el('div', 'menu');
      for (const [text, handler] of [
        ['View details', () => showView(user.id, toggle)],
        ['Edit user', () => showEditor(user.id, toggle)],
        ['Delete user', () => confirmDelete([user.id], toggle)]
      ]) {
        const action = button(text, `menu-item${text === 'Delete user' ? ' fg-danger' : ''}`);
        action.addEventListener('click', handler);
        menu.append(action);
      }
      menuWrapper.append(toggle, menu);
      actions.append(menuWrapper);
      row.append(actions);
      row.dataset.selected = String(selected.has(user.id));
      body.append(row);
    }
    const pageIds = result.rows.map((user) => user.id);
    selectAll.checked = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    selectAll.indeterminate = pageIds.some((id) => selected.has(id)) && !selectAll.checked;
    selectAll.disabled = !pageIds.length;
    container.querySelector('[data-users-empty]').hidden = result.total > 0;
    container.querySelector('[data-users-count]').textContent =
      `${result.total} ${result.total === 1 ? 'user' : 'users'}`;
    container.querySelector('[data-users-summary]').textContent =
      `${result.total ? (result.page - 1) * state.pageSize + 1 : 0}–${Math.min(result.page * state.pageSize, result.total)} of ${result.total} users`;
    container.querySelector('[data-users-page]').textContent = `Page ${result.page} of ${result.pages}`;
    container.querySelector('[data-users-prev]').disabled = result.page === 1;
    container.querySelector('[data-users-next]').disabled = result.page === result.pages;
    container.querySelector('[data-users-bulk]').hidden = selected.size === 0;
    container.querySelector('[data-users-selected]').textContent = `${selected.size} selected`;
    for (const header of container.querySelectorAll('[data-users-sort-header]'))
      header.setAttribute(
        'aria-sort',
        header.dataset.usersSortHeader === state.sortKey
          ? state.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      );
  };
  body.addEventListener('change', (event) => {
    const check = event.target.closest('[data-user-select]');
    if (!check) return;
    const id = Number(check.dataset.userSelect);
    if (check.checked) selected.add(id);
    else selected.delete(id);
    render();
    container.querySelector(`[data-user-select="${id}"]`)?.focus();
  });
  selectAll.addEventListener('change', () => {
    for (const user of queryAdminRows(users, state).rows) {
      if (selectAll.checked) selected.add(user.id);
      else selected.delete(user.id);
    }
    render();
  });
  container.querySelector('[data-users-search]').addEventListener('input', (event) => {
    state.query = event.target.value;
    state.page = 1;
    render();
  });
  container.querySelector('[data-users-status]').addEventListener('change', (event) => {
    state.status = event.target.value;
    state.page = 1;
    render();
  });
  container.querySelectorAll('[data-users-clear]').forEach((control) =>
    control.addEventListener('click', () => {
      state.query = '';
      state.status = '';
      state.page = 1;
      container.querySelector('[data-users-search]').value = '';
      container.querySelector('[data-users-status]').value = '';
      render();
    })
  );
  container.querySelectorAll('[data-users-sort]').forEach((control) =>
    control.addEventListener('click', () => {
      state.direction = state.sortKey === control.dataset.usersSort && state.direction === 'asc' ? 'desc' : 'asc';
      state.sortKey = control.dataset.usersSort;
      render();
    })
  );
  container.querySelector('[data-users-page-size]').addEventListener('change', (event) => {
    state.pageSize = Number(event.target.value);
    state.page = 1;
    render();
  });
  container.querySelector('[data-users-prev]').addEventListener('click', () => {
    state.page--;
    render();
  });
  container.querySelector('[data-users-next]').addEventListener('click', () => {
    state.page++;
    render();
  });
  container.querySelector('[data-users-deselect]').addEventListener('click', () => {
    selected.clear();
    render();
    selectAll.focus();
  });
  container.querySelector('[data-users-activate]').addEventListener('click', () => {
    const count = selected.size;
    users = users.map((user) => (selected.has(user.id) ? { ...user, status: 'Active' } : user));
    selected.clear();
    render();
    feedback(`${count} demo users set to active on this page only.`);
  });
  container
    .querySelector('[data-users-delete]')
    .addEventListener('click', (event) => confirmDelete([...selected], event.currentTarget));
  root.querySelector('[data-user-add]').addEventListener('click', (event) => showEditor(null, event.currentTarget));
  view.querySelector('[data-user-edit-current]').addEventListener('click', () => showEditor(currentId, returnControl));
  deletion.querySelector('[data-users-confirm-delete]').addEventListener('click', () => {
    users = users.filter((user) => !deleteIds.includes(user.id));
    for (const id of deleteIds) selected.delete(id);
    api.Dialog.getOrCreateInstance(deletion).hide();
    render();
    feedback(`${deleteIds.length} demo ${deleteIds.length === 1 ? 'user' : 'users'} deleted on this page only.`);
  });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const { value, errors } = validateAdminUser(Object.fromEntries(new FormData(form)), users, editingId);
    for (const key of ['name', 'email']) {
      form.querySelector(`[data-user-error="${key}"]`).textContent = errors[key] || '';
      form.elements.namedItem(key).setAttribute('aria-invalid', String(Boolean(errors[key])));
    }
    if (Object.keys(errors).length) {
      form.elements.namedItem(Object.keys(errors)[0]).focus();
      return;
    }
    if (editingId === null)
      users.push({ ...value, id: Math.max(0, ...users.map((user) => user.id)) + 1, avatar: 1, lastSeen: 'Never' });
    else users = users.map((user) => (user.id === editingId ? { ...user, ...value } : user));
    selected.clear();
    state.query = '';
    state.status = '';
    container.querySelector('[data-users-search]').value = '';
    container.querySelector('[data-users-status]').value = '';
    api.Dialog.getOrCreateInstance(editor).hide();
    render();
    feedback(
      `${value.name} ${editingId === null ? 'added to' : 'updated in'} this page’s demo directory. No account was changed remotely.`
    );
  });
  render();
}

async function createFixtureDialog(api, { title, description, trigger, fields, confirm, onConfirm }) {
  const dialog = el('dialog', 'dialog');
  dialog.setAttribute('aria-label', title);
  const header = el('div', 'dialog-header');
  header.append(el('h2', '', title));
  const close = button('×', 'btn-text admin-icon');
  close.dataset.bsDismiss = 'dialog';
  close.setAttribute('aria-label', 'Close dialog');
  header.append(close);
  const form = el('form');
  form.dataset.adminHandled = '';
  const content = el('div', 'dialog-body admin-form-stack');
  if (description) content.append(el('p', 'fg-2', description));
  const controls = [];
  if (fields)
    fields.forEach(([label, value], index) => {
      const field = el('div');
      const input = el('input', 'form-control');
      input.id = `admin-fixture-field-${index}`;
      input.name = `field-${index}`;
      input.value = value;
      input.required = true;
      const caption = el('label', 'form-label', label);
      caption.htmlFor = input.id;
      field.append(caption, input);
      content.append(field);
      controls.push(input);
    });
  const footer = el('div', 'dialog-footer');
  const cancel = button(confirm ? 'Cancel' : 'Close', 'btn-outline theme-secondary');
  cancel.dataset.bsDismiss = 'dialog';
  footer.append(cancel);
  if (confirm) {
    const submit = button(confirm, confirm.includes('Delete') ? 'btn-solid theme-danger' : 'btn-solid theme-primary');
    submit.type = 'submit';
    footer.append(submit);
  }
  form.append(header, content, footer);
  dialog.append(form);
  document.body.append(dialog);
  const instance = api.Dialog.getOrCreateInstance(dialog);
  const focusTarget = await prepareAdminDialog(api, trigger);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    onConfirm?.(controls.map((control) => control.value.trim()));
    instance.hide();
  });
  dialog.addEventListener(
    'hidden.bs.dialog',
    () => {
      instance.dispose();
      dialog.remove();
      const returnTo = [
        focusTarget,
        document.querySelector('[data-media-upload]'),
        document.querySelector('[data-user-add]'),
        document.getElementById('admin-content')
      ].find(
        (element) =>
          element?.isConnected &&
          element.getClientRects().length &&
          window.getComputedStyle(element).visibility !== 'hidden'
      );
      returnTo?.focus();
    },
    { once: true }
  );
  await showAdminDialog(api, dialog, focusTarget);
  return content;
}

function fixtureFieldTarget(cell) {
  return (
    cell.querySelector('[data-admin-field-value], strong, h2, h3, h4, h5, h6') ||
    cell.querySelector('a, .fw-semibold') ||
    cell.querySelector('.admin-status-badge, .badge') ||
    cell
  );
}

function initializeFixtureTables(root, api, announce) {
  root.querySelectorAll('.content-wrapper table:not([data-admin-users] table)').forEach((table) => {
    if (table.closest('[data-admin-users]')) return;
    const scope = table.closest('[data-admin-table]') || table.closest('.content-wrapper');
    const tbody = table.tBodies[0];
    if (!tbody) return;
    let rows = [...tbody.rows];
    const rowTemplate = rows[0]?.cloneNode(true);
    let page = 1;
    const pageSize = 10;
    let sortColumn = -1;
    let ascending = true;
    let sortAsDate = false;
    const controls = [...scope.querySelectorAll('input[placeholder*="earch"], [data-table-search]')].filter(
      (input) => !input.closest('dialog, form')
    );
    const filters = [...scope.querySelectorAll('[data-table-status], select')].filter(
      (select) =>
        !select.closest('dialog, form') &&
        (select.matches('[data-table-status]') ||
          /status|categor|author/i.test(select.getAttribute('aria-label') || select.parentElement.textContent))
    );
    const footer = el('div', 'admin-table-footer');
    const summary = el('span');
    summary.setAttribute('aria-live', 'polite');
    const nav = el('nav');
    nav.setAttribute('aria-label', 'Table pagination');
    const prev = button('Previous');
    const next = button('Next');
    const pageLabel = el('span');
    nav.append(prev, pageLabel, next);
    footer.append(summary, nav);
    table.closest('.table-responsive')?.after(footer);
    scope.querySelectorAll('.pagination').forEach((pagination) => {
      pagination.hidden = true;
    });
    const empty = el('div', 'admin-empty', 'No matching records. Try a different search or filter.');
    empty.hidden = true;
    footer.before(empty);
    const bulk = el('div', 'admin-bulk-bar');
    bulk.hidden = true;
    const selectedLabel = el('span');
    const remove = button('Delete selected', 'btn-outline theme-danger btn-sm');
    bulk.append(selectedLabel, remove);
    footer.before(bulk);
    const headerCheck = table.tHead?.querySelector('input[type="checkbox"]');
    const render = () => {
      const query = controls.map((input) => input.value.trim().toLocaleLowerCase()).filter(Boolean);
      const terms = filters
        .map((select) => {
          const option = select.selectedOptions[0];
          return select.selectedIndex > 0 ? option.textContent.trim().toLocaleLowerCase() : '';
        })
        .filter(Boolean);
      let matching = rows.filter(
        (row) =>
          (!scope.querySelector('#showArchived') ||
            scope.querySelector('#showArchived').checked ||
            !/archived/i.test(row.textContent)) &&
          [...query, ...terms].every((term) => row.textContent.toLocaleLowerCase().includes(term))
      );
      if (sortColumn >= 0)
        matching.sort((a, b) => {
          const left = fixtureFieldTarget(a.cells[sortColumn]).textContent.trim();
          const right = fixtureFieldTarget(b.cells[sortColumn]).textContent.trim();
          const dates = [Date.parse(left), Date.parse(right)];
          const comparison =
            sortAsDate && dates.every(Number.isFinite)
              ? dates[0] - dates[1]
              : left.localeCompare(right, undefined, { numeric: true });
          return comparison * (ascending ? 1 : -1);
        });
      const pages = Math.max(1, Math.ceil(matching.length / pageSize));
      page = Math.max(1, Math.min(page, pages));
      const visible = matching.slice((page - 1) * pageSize, page * pageSize);
      for (const row of rows) row.hidden = !visible.includes(row);
      for (const row of matching) tbody.append(row);
      summary.textContent = `${matching.length ? (page - 1) * pageSize + 1 : 0}–${Math.min(page * pageSize, matching.length)} of ${matching.length} records`;
      pageLabel.textContent = `Page ${page} of ${pages}`;
      prev.disabled = page === 1;
      next.disabled = page === pages;
      empty.hidden = matching.length > 0;
      const checks = visible.flatMap((row) => [...row.querySelectorAll('input[type="checkbox"]')]);
      if (headerCheck) {
        headerCheck.checked = checks.length > 0 && checks.every((check) => check.checked);
        headerCheck.indeterminate = checks.some((check) => check.checked) && !headerCheck.checked;
        headerCheck.disabled = !checks.length;
      }
      const selected = rows.filter((row) => row.querySelector('input[type="checkbox"]:checked'));
      bulk.hidden = !selected.length;
      selectedLabel.textContent = `${selected.length} selected`;
      scope
        .querySelector('[data-table-summary]')
        ?.replaceChildren(document.createTextNode(`${matching.length} sample records`));
    };
    controls.forEach((input) => {
      input.setAttribute('aria-label', input.getAttribute('aria-label') || 'Search table');
      input.addEventListener('input', () => {
        page = 1;
        render();
      });
    });
    filters.forEach((select) => {
      select.setAttribute('aria-label', select.getAttribute('aria-label') || 'Filter table');
      select.addEventListener('change', () => {
        page = 1;
        render();
      });
    });
    scope.querySelectorAll('[data-table-filter-status]').forEach((control) =>
      control.addEventListener('click', (event) => {
        event.preventDefault();
        const select = filters.find((input) =>
          [...input.options].some(
            (option) =>
              option.textContent.trim().toLocaleLowerCase() === control.dataset.tableFilterStatus.toLocaleLowerCase()
          )
        );
        if (!select) return;
        const option = [...select.options].find(
          (item) =>
            item.textContent.trim().toLocaleLowerCase() === control.dataset.tableFilterStatus.toLocaleLowerCase()
        );
        select.value = option.value;
        page = 1;
        render();
        table.scrollIntoView({ block: 'center' });
        table.focus({ preventScroll: true });
        announce(`Showing ${control.dataset.tableFilterStatus.toLocaleLowerCase()} demo records.`);
      })
    );
    prev.addEventListener('click', () => {
      page--;
      render();
    });
    next.addEventListener('click', () => {
      page++;
      render();
    });
    headerCheck?.addEventListener('change', () => {
      for (const row of rows.filter((row) => !row.hidden))
        row.querySelectorAll('input[type="checkbox"]').forEach((check) => {
          check.checked = headerCheck.checked;
        });
      render();
    });
    for (const [index, header] of [...(table.tHead?.rows[0]?.cells || [])].entries()) {
      if (!header.textContent.trim() || /actions/i.test(header.textContent) || header.querySelector('input, button'))
        continue;
      const text = header.textContent.trim();
      const sort = button(`${text} ↕`, 'admin-sort');
      header.replaceChildren(sort);
      header.setAttribute('aria-sort', 'none');
      header.scope = 'col';
      sort.addEventListener('click', () => {
        ascending = index !== sortColumn || !ascending;
        sortColumn = index;
        sortAsDate = /date|updated|last used|created/i.test(text);
        table.tHead.querySelectorAll('[aria-sort]').forEach((th) => th.setAttribute('aria-sort', 'none'));
        header.setAttribute('aria-sort', ascending ? 'ascending' : 'descending');
        render();
      });
    }
    const sortSelect = scope.querySelector('select[aria-label="Sort"]');
    sortSelect?.addEventListener('change', () => {
      const headers = [...(table.tHead?.rows[0]?.cells || [])];
      const byName = sortSelect.value === 'a-z';
      sortColumn = headers.findIndex((header) =>
        (byName ? /title|name/i : /updated|date|created/i).test(header.textContent)
      );
      ascending = byName || sortSelect.value === 'oldest';
      sortAsDate = !byName;
      if (!['a-z', 'oldest', 'newest'].includes(sortSelect.value)) sortColumn = -1;
      headers.forEach((header, index) => {
        if (header.hasAttribute('aria-sort'))
          header.setAttribute('aria-sort', index === sortColumn ? (ascending ? 'ascending' : 'descending') : 'none');
      });
      page = 1;
      render();
    });
    tbody.addEventListener('change', render);
    const deleteRows = (targets, trigger) =>
      createFixtureDialog(api, {
        title: 'Delete demo records?',
        description: `Remove ${targets.length} selected record(s) from this page only. Reloading restores the sample data.`,
        trigger,
        confirm: 'Delete demo records',
        onConfirm: () => {
          targets.forEach((row) => row.remove());
          rows = rows.filter((row) => !targets.includes(row));
          render();
          announce('Demo records deleted on this page only.');
        }
      });
    remove.addEventListener('click', () =>
      deleteRows(
        rows.filter((row) => row.querySelector('input[type="checkbox"]:checked')),
        remove
      )
    );
    const bindRow = (row) => {
      row
        .querySelectorAll('input[type="checkbox"]')
        .forEach((check) =>
          check.setAttribute(
            'aria-label',
            `Select ${row.cells[check.closest('td').cellIndex + 1]?.textContent.trim() || 'record'}`
          )
        );
      for (const action of row.querySelectorAll('a[href="#"], button:not([data-bs-toggle])')) {
        const text = (
          action.textContent +
          ' ' +
          (action.getAttribute('title') || '') +
          ' ' +
          (action.getAttribute('aria-label') || '')
        ).trim();
        const icon = action.querySelector('i')?.className || '';
        const kind = /delet|trash/i.test(text + icon) ? 'delete' : /edit|pencil/i.test(text + icon) ? 'edit' : 'view';
        action.dataset.adminHandled = '';
        if (!action.textContent.trim()) action.setAttribute('aria-label', `${kind} record`);
        action.addEventListener('click', (event) => {
          event.preventDefault();
          if (kind === 'delete') {
            deleteRows([row], action);
            return;
          }
          const cells = [...row.cells].filter(
            (cell) => cell.textContent.trim() && !cell.querySelector('button, .menu, input')
          );
          if (kind === 'edit')
            createFixtureDialog(api, {
              title: 'Edit demo record',
              description: 'These changes apply to this page only.',
              trigger: action,
              fields: cells.map((cell) => [
                table.tHead.rows[0].cells[cell.cellIndex]?.textContent.replace('↕', '').trim() || 'Value',
                fixtureFieldTarget(cell).textContent.trim()
              ]),
              confirm: 'Save demo record',
              onConfirm: (values) => {
                cells.forEach((cell, index) => {
                  fixtureFieldTarget(cell).textContent = values[index];
                });
                render();
                announce('Demo record updated on this page only.');
              }
            });
          else
            createFixtureDialog(api, {
              title: action.dataset.demoDetail || 'Record details',
              description:
                row.textContent.trim().replace(/\s+/g, ' ') + ' · Sample data. No remote action is performed.',
              trigger: action
            });
        });
      }
    };
    rows.forEach(bindRow);
    fixtureCreators.set(table, (name) => {
      if (!rowTemplate) return false;
      const row = rowTemplate.cloneNode(true);
      row.hidden = false;
      const identifier = ++fixtureIdentifier;
      const ids = new Map();
      row.querySelectorAll('[id]').forEach((element) => {
        const next = `${element.id}-fixture-${identifier}`;
        ids.set(element.id, next);
        element.id = next;
      });
      row.querySelectorAll('[for], [aria-controls], [aria-labelledby]').forEach((element) => {
        for (const attribute of ['for', 'aria-controls', 'aria-labelledby']) {
          if (element.hasAttribute(attribute))
            element.setAttribute(
              attribute,
              element
                .getAttribute(attribute)
                .split(/\s+/)
                .map((value) => ids.get(value) || value)
                .join(' ')
            );
        }
      });
      row.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      const cell = [...row.cells].find(
        (item) => !item.querySelector('input, button, .menu') && item.textContent.trim()
      );
      if (!cell) return false;
      fixtureFieldTarget(cell).textContent = name;
      row.querySelectorAll('[data-bs-toggle="menu"]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        api.Menu.getOrCreateInstance(toggle);
      });
      bindRow(row);
      tbody.prepend(row);
      rows.unshift(row);
      page = 1;
      controls.forEach((input) => {
        input.value = '';
      });
      filters.forEach((select) => {
        select.selectedIndex = 0;
      });
      render();
      return true;
    });
    const showArchived = scope.querySelector('#showArchived');
    showArchived?.addEventListener('change', () => {
      rows.forEach((row) => {
        if (/archived/i.test(row.textContent)) row.dataset.archived = 'true';
      });
      render();
    });
    render();
  });
}

function initializeForms(root, api, announce) {
  root.querySelectorAll('form:not([data-user-form]):not([data-admin-handled])').forEach((form, index) => {
    form.noValidate = true;
    const feedback = el('div', 'admin-feedback');
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    form.append(feedback);
    form.querySelectorAll('input, select, textarea').forEach((control, fieldIndex) => {
      if (!control.id) control.id = `admin-form-${index}-${fieldIndex}`;
      const container = control.closest('.mb-5, .mb-3, .mb-7') || control.parentElement;
      const label = container.querySelector('label');
      if (label && !label.htmlFor) label.htmlFor = control.id;
      if (!label && !control.getAttribute('aria-label') && control.type !== 'hidden')
        control.setAttribute('aria-label', control.placeholder || control.name || 'Demo field');
      if (
        ['text', 'email', 'password'].includes(control.type) &&
        !control.disabled &&
        !control.readOnly &&
        !/search|bio|phone|optional|subject|company/i.test(control.id + control.placeholder)
      )
        control.required = true;
    });
    form.querySelectorAll('a[href="#"]').forEach((action) =>
      action.addEventListener('click', (event) => {
        event.preventDefault();
        createFixtureDialog(api, {
          title: action.getAttribute('aria-label') || 'Demo sign in',
          description: 'This is a frontend demo. No authentication provider is connected and nothing was sent.',
          trigger: action
        });
      })
    );
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      let firstInvalid = null;
      const passwordFields = [...form.querySelectorAll('input[type="password"], [data-au-password] input')];
      if (passwordFields.length > 1)
        passwordFields
          .at(-1)
          .setCustomValidity(passwordFields[0].value === passwordFields.at(-1).value ? '' : 'Passwords must match.');
      for (const control of form.querySelectorAll('input, select, textarea')) {
        const valid = control.checkValidity();
        control.setAttribute('aria-invalid', String(!valid));
        const errorId = `${control.id}-feedback`;
        let error = document.getElementById(errorId);
        if (!valid) {
          if (!error) {
            error = el('span', 'admin-field-error');
            error.id = errorId;
            control.after(error);
            control.setAttribute(
              'aria-describedby',
              [control.getAttribute('aria-describedby'), errorId].filter(Boolean).join(' ')
            );
          }
          error.textContent = control.validationMessage;
          firstInvalid ||= control;
        } else if (error) error.textContent = '';
      }
      if (firstInvalid) {
        feedback.textContent = 'Please correct the highlighted fields.';
        firstInvalid.focus();
        return;
      }
      feedback.textContent =
        'Valid demo form. Your changes apply to this page only; nothing was sent or saved remotely.';
      announce(feedback.textContent);
    });
    form
      .querySelectorAll(
        'button[type="button"]:not([data-bs-toggle]):not([data-bs-dismiss]):not([data-au-password-toggle])'
      )
      .forEach((action) => {
        const text = action.textContent.trim();
        if (/delete|deactivate/i.test(text))
          action.addEventListener('click', () =>
            createFixtureDialog(api, {
              title: `${text} demo account?`,
              description: 'This only changes the demo status shown on this page. No real account is affected.',
              trigger: action,
              confirm: /delete/i.test(text) ? 'Delete demo account' : 'Deactivate demo account',
              onConfirm: () => {
                feedback.textContent = `Demo account ${/delete/i.test(text) ? 'deleted' : 'deactivated'} on this page only. Reload to reset.`;
                announce(feedback.textContent);
              }
            })
          );
        else if (/reset|cancel/i.test(text))
          action.addEventListener('click', () => {
            form.reset();
            feedback.textContent = 'Demo form reset to its initial values.';
          });
        else if (/save|update|submit|backup/i.test(text)) action.addEventListener('click', () => form.requestSubmit());
      });
  });
}

function initializeFixtureActions(root, api, announce) {
  const content = root.querySelector('.content-wrapper');
  if (!content) return;
  content
    .querySelectorAll('button:not([data-admin-handled]), a[href="#"]:not([data-admin-handled])')
    .forEach((action) => {
      if (
        action.closest('[data-admin-users], [data-admin-media], table, form, dialog, .admin-table-footer') ||
        action.dataset.bsToggle ||
        [...action.attributes].some((attribute) =>
          /^(data-users|data-user|data-media|data-table|data-bs-)/.test(attribute.name)
        )
      )
        return;
      const initialText = action.textContent.trim();
      if (!initialText && !action.getAttribute('aria-label'))
        action.setAttribute(
          'aria-label',
          /search/.test(action.querySelector('i')?.className || '') ? 'Search table' : 'Demo action'
        );
      if (/search|filter/i.test(initialText + (action.querySelector('i')?.className || ''))) return;
      action.addEventListener('click', (event) => {
        event.preventDefault();
        const text = action.textContent.trim();
        const card = action.closest('.card');
        if (/^(activate|deactivate|activated)$/i.test(text) && card) {
          const active = !/^deactivate$/i.test(text);
          if (/settings\/themes/.test(location.pathname)) {
            content.querySelectorAll('.card button').forEach((control) => {
              if (/^activated$/i.test(control.textContent.trim())) {
                control.disabled = false;
                control.textContent = 'Activate';
              }
            });
            content.querySelectorAll('.card .badge').forEach((badge) => {
              if (badge.textContent.trim() === 'Active') badge.remove();
            });
            action.textContent = 'Activated';
            action.disabled = true;
          } else {
            action.textContent = active ? 'Deactivate' : 'Activate';
            const badge = card.querySelector('.badge');
            if (badge) {
              badge.textContent = active ? 'Active' : 'Inactive';
              badge.className = 'admin-status-badge';
              badge.dataset.status = active ? 'Active' : 'Inactive';
            }
          }
          let feedback = card.querySelector('[data-card-feedback]');
          if (!feedback) {
            feedback = el('p', 'admin-demo-note');
            feedback.dataset.cardFeedback = '';
            feedback.setAttribute('role', 'status');
            card.querySelector('.card-body')?.append(feedback);
          }
          feedback.textContent = 'Demo state changed on this page only.';
          announce(feedback.textContent);
          return;
        }
        if (/uninstall|trash|delete/i.test(text + (action.querySelector('i')?.className || '')) && card) {
          createFixtureDialog(api, {
            title: 'Remove demo item?',
            description: 'This removes the sample item from this page only. Reloading restores it.',
            trigger: action,
            confirm: 'Delete demo item',
            onConfirm: () => {
              card.remove();
              announce('Demo item removed from this page only.');
            }
          });
          return;
        }
        if (/^(annual|monthly) billing$/i.test(text) && card) {
          const annual = /^annual/i.test(text);
          action
            .closest('ul')
            .querySelectorAll('a')
            .forEach((link) => {
              link.classList.toggle('active', link === action);
              link.setAttribute('aria-pressed', String(link === action));
            });
          const price = card.querySelector('.fs-4xl');
          if (price) price.textContent = annual ? '₹2,099' : '₹2,499';
          const period = price?.nextElementSibling;
          if (period) period.textContent = annual ? '/ user / month billed annually' : '/ user / month billed monthly';
          announce('Demo billing interval changed. Prices are illustrative.');
          return;
        }
        if (/save changes/i.test(text)) {
          content.querySelectorAll('form').forEach((form) => form.requestSubmit());
          return;
        }
        if (/reset to defaults/i.test(text)) {
          content.querySelectorAll('form').forEach((form) => {
            form.reset();
            form.querySelector('[role="status"]').textContent = 'Demo fields reset to their initial values.';
          });
          announce('Demo forms reset.');
          return;
        }
        if (/add|create|write|new/i.test(text)) {
          const form = content.querySelector('form');
          if (form) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
            form.querySelector('input,textarea,select')?.focus();
            announce('Complete the demo form below.');
          } else
            createFixtureDialog(api, {
              title: text || 'Add demo record',
              description: 'This creates a page-local sample record. Reloading resets the demo.',
              fields: [['Name', '']],
              trigger: action,
              confirm: 'Add demo record',
              onConfirm: (values) => {
                const table = content.querySelector('table');
                const created = table && fixtureCreators.get(table)?.(values[0]);
                announce(
                  created
                    ? `${values[0]} added to this page’s demo only.`
                    : 'No demo record was created: this table has no sample schema.'
                );
              }
            });
        } else
          createFixtureDialog(api, {
            title: text || 'Demo action',
            description: 'This is a frontend demonstration. No service is connected and no remote changes are made.',
            trigger: action
          });
      });
    });
}

function initializeMedia(root, api, announce) {
  const container = root.querySelector('[data-admin-media]');
  if (!container) return;
  let files = JSON.parse(container.querySelector('[data-media-fixture]').textContent);
  const selected = new Set();
  const grid = container.querySelector('[data-media-grid]');
  const state = { query: '', sortKey: 'date', direction: 'desc', page: 1, pageSize: 6 };
  const search = container.querySelector('[data-media-search]');
  const type = container.querySelector('[data-media-type]');
  const input = container.querySelector('[data-media-file-input]');
  const feedback = (message) => {
    container.querySelector('[data-media-feedback]').textContent = message;
    announce(message);
  };
  const render = () => {
    const result = queryAdminRows(
      files.filter((file) => !type.value || file.type === type.value),
      state
    );
    state.page = result.page;
    grid.replaceChildren();
    for (const file of result.rows) {
      const card = el('article', 'card admin-media-card');
      const preview = el('div', 'admin-media-preview');
      if (file.src) {
        const image = el('img');
        image.src = resolveAdminAsset(file.src);
        image.alt = '';
        preview.append(image);
      } else
        preview.append(
          el(
            'i',
            file.type === 'Video' ? 'ri-movie-line' : file.type === 'Audio' ? 'ri-volume-up-line' : 'ri-file-text-line'
          )
        );
      const check = el('input', 'check');
      check.type = 'checkbox';
      check.checked = selected.has(file.id);
      check.dataset.mediaSelect = file.id;
      check.setAttribute('aria-label', `Select ${file.name}`);
      check.addEventListener('change', () => {
        if (check.checked) selected.add(file.id);
        else selected.delete(file.id);
        render();
        container.querySelector(`[data-media-select="${file.id}"]`)?.focus();
      });
      preview.append(check);
      const body = el('div', 'card-body');
      body.append(el('h2', '', file.name), el('p', 'fg-2', `${file.type} · ${file.size} KB`));
      const actions = el('div', 'admin-media-actions');
      const rename = button('Rename', 'btn-text btn-sm');
      const view = button('Details', 'btn-text btn-sm');
      rename.addEventListener('click', () =>
        createFixtureDialog(api, {
          title: 'Rename demo file',
          description: 'The local preview name changes on this page only.',
          trigger: rename,
          fields: [['File name', file.name]],
          confirm: 'Rename demo file',
          onConfirm: (values) => {
            file.name = values[0];
            render();
            feedback('Demo file renamed on this page only.');
          }
        })
      );
      view.addEventListener('click', () =>
        createFixtureDialog(api, {
          title: file.name,
          description: `${file.type} · ${file.size} KB · Added ${file.date}. This is a local demo preview.`,
          trigger: view
        })
      );
      actions.append(rename, view);
      body.append(actions);
      card.append(preview, body);
      grid.append(card);
    }
    container.querySelector('[data-media-count]').textContent = `${result.total} files`;
    container.querySelector('[data-media-summary]').textContent =
      `${result.total ? (result.page - 1) * 6 + 1 : 0}–${Math.min(result.page * 6, result.total)} of ${result.total} files`;
    container.querySelector('[data-media-page]').textContent = `Page ${result.page} of ${result.pages}`;
    container.querySelector('[data-media-prev]').disabled = result.page === 1;
    container.querySelector('[data-media-next]').disabled = result.page === result.pages;
    container.querySelector('[data-media-empty]').hidden = result.total > 0;
    container.querySelector('[data-media-bulk]').hidden = selected.size === 0;
    container.querySelector('[data-media-selected]').textContent = `${selected.size} selected`;
  };
  root.querySelector('[data-media-upload]').addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    for (const file of input.files) {
      const fileType = file.type.startsWith('image/')
        ? 'Image'
        : file.type.startsWith('video/')
          ? 'Video'
          : file.type.startsWith('audio/')
            ? 'Audio'
            : 'Document';
      files.push({
        id: Math.max(0, ...files.map((item) => item.id)) + 1,
        name: file.name,
        type: fileType,
        size: Math.max(1, Math.round(file.size / 1024)),
        date: new Date().toISOString().slice(0, 10),
        src: fileType === 'Image' ? URL.createObjectURL(file) : ''
      });
    }
    const count = input.files.length;
    input.value = '';
    state.page = 1;
    state.query = '';
    search.value = '';
    type.value = '';
    render();
    feedback(`${count} local file(s) added to this tab’s preview. Nothing was uploaded.`);
  });
  search.addEventListener('input', () => {
    state.query = search.value;
    state.page = 1;
    render();
  });
  type.addEventListener('change', () => {
    state.page = 1;
    render();
  });
  container.querySelector('[data-media-sort]').addEventListener('change', (event) => {
    [state.sortKey, state.direction] = event.target.value.split(':');
    state.page = 1;
    render();
  });
  container.querySelector('[data-media-prev]').addEventListener('click', () => {
    state.page--;
    render();
  });
  container.querySelector('[data-media-next]').addEventListener('click', () => {
    state.page++;
    render();
  });
  container.querySelector('[data-media-clear]').addEventListener('click', () => {
    selected.clear();
    render();
  });
  container.querySelector('[data-media-delete]').addEventListener('click', (event) =>
    createFixtureDialog(api, {
      title: 'Delete selected demo files?',
      description: `Remove ${selected.size} selected file(s) from this page’s preview only.`,
      trigger: event.currentTarget,
      confirm: 'Delete demo files',
      onConfirm: () => {
        files.forEach((file) => {
          if (selected.has(file.id) && file.src?.startsWith('blob:')) URL.revokeObjectURL(file.src);
        });
        files = files.filter((file) => !selected.has(file.id));
        selected.clear();
        render();
        feedback('Selected files removed from this page’s demo only.');
      }
    })
  );
  window.addEventListener(
    'pagehide',
    () =>
      files.forEach((file) => {
        if (file.src?.startsWith('blob:')) URL.revokeObjectURL(file.src);
      }),
    { once: true }
  );
  render();
}
